// Pure mapping layer for the Shared Items resync view. No React, no hooks, no side effects —
// everything here is derivable straight from props the parent already holds, which is what lets
// SharedColumns/ExpandedRow call these directly instead of routing data through render-time state.
import { Time } from "@vibe/icons";
import { BoardHeader, DiscrepancyGridRow, DisplayValue } from "./DiscrepancyGrid/discrepancyGrid.interface";
import { columnValuesResync, NAME_COLUMN_TYPE } from "./resynchronization.interface";

/**
 * That board's column_value for a (columnType, columnTitle) pair, or undefined when the board
 * has no such column. Shared by buildColumnUpdates and toDiscrepancyRows so the resync payload
 * and the displayed value can never be matched against different columns.
 */
const findDiscrepancyItem = (dbItem: any, discrepancyItems: any[]) =>
  discrepancyItems.find(
    (discrepancy: any) => discrepancy.item_id.toString() === dbItem.item_id.toString(),
  );

const findMatchingColumn = (
  dbItem: any,
  discrepancyItems: any[],
  columnType: string,
  columnTitle: string,
) => {
  const discrepancyItem = findDiscrepancyItem(dbItem, discrepancyItems);

  // The name is not among column_values — it is the item's own `name`. Presented in the
  // column shape the rest of this module reads, with Monday's "name" pseudo-column id, so
  // the display and the resync payload go through the same matching as every column.
  if (columnType === NAME_COLUMN_TYPE) {
    if (!discrepancyItem) return undefined;
    return {
      id: "name",
      type: NAME_COLUMN_TYPE,
      column: { title: columnTitle },
      value: discrepancyItem.name ?? "",
      text: discrepancyItem.name ?? "",
      updated_at: discrepancyItem.updated_at ?? null,
    };
  }

  return discrepancyItem?.column_values.find(
    (column: any) => column.type === columnType && column.column.title === columnTitle,
  );
};

/**
 * Per-board update payload for a single (columnType, columnTitle) pair. Lifted verbatim from
 * ColumnValue.tsx's former useMemo body — same matching rules, same "does not exist" fallback,
 * same dropdown `values`-over-`value` preference (dropdown's resync payload is its ids/labels
 * array, not the raw `value` string).
 */
const toColumnUpdate = (
  dbItem: any,
  matchingColumn: any,
  columnType: string,
  columnTitle: string,
): columnValuesResync => {
  // Handle case when no matching column exists
  if (!matchingColumn) {
    return {
      item_id: dbItem.item_id,
      board_id: dbItem.board_id,
      column_type: columnType,
      column_title: columnTitle,
      column_id: null,
      value: null,
      text: `Column does not exist in board`,
      exists: false,
    };
  }

  return {
    item_id: dbItem.item_id,
    board_id: dbItem.board_id,
    column_type: columnType,
    column_title: columnTitle,
    column_id: matchingColumn.id || null,
    value: columnType === "dropdown" ? matchingColumn.values : matchingColumn.value,
    text: matchingColumn.text,
    exists: true,
    ...(columnType === NAME_COLUMN_TYPE ? { updated_at: matchingColumn.updated_at ?? null } : {}),
  };
};

export const buildColumnUpdates = (
  dbItems: any[],
  discrepancyItems: any[],
  columnType: string,
  columnTitle: string,
): columnValuesResync[] =>
  dbItems.map((item: any) =>
    toColumnUpdate(
      item,
      findMatchingColumn(item, discrepancyItems, columnType, columnTitle),
      columnType,
      columnTitle,
    ),
  );

/**
 * Header row for the boards a synced item spans. Lifted from BoardsColumn.tsx: prefer the
 * loaded board name, fall back to the cached one, and only say "Unknown Board" once loading
 * has genuinely finished (rather than mid-fetch, when the name is merely not in yet).
 *
 * `boardId` is keyed the same way `buildColumnUpdates`/`toDiscrepancyRows` key their per-board
 * maps — `String(dbItem.board_id)` — so a caller can join the two by boardId without a type
 * mismatch silently blanking every cell.
 */
/** Verbatim from BoardsColumn.tsx, which paired it with an Info icon on "Unknown Board". */
const BOARD_ACCESS_INFO = "You might not have access to view this board's data";

export const toBoardHeaders = (
  dbItems: any[],
  discrepancyItems: any[],
  isLoading = false,
): BoardHeader[] => {
  const boardByItemId = new Map<string, any>();
  (discrepancyItems ?? []).forEach((board: any) => {
    if (board?.item_id !== undefined && board?.item_id !== null) {
      boardByItemId.set(board.item_id.toString(), board);
    }
  });

  return dbItems.map((item: any) => {
    const board = boardByItemId.get(item.item_id.toString());

    // Prefer the loaded value's board name; fall back to the name carried on the cached row
    // (available before values are lazily fetched).
    const resolvedName = board?.board?.name ?? item?.board_name;

    // No name yet AND still fetching → the caller should render a placeholder, not "Unknown".
    if (!resolvedName && isLoading) {
      return {
        boardId: String(item.board_id),
        boardName: "",
        isResolving: true,
      };
    }

    // Name unresolved after loading → the board is genuinely inaccessible. BoardsColumn used
    // to pair that with an Info icon explaining why; without it the user sees "Unknown Board"
    // AND "Column does not exist on this board" in every cell, with nothing tying the two
    // together. Copy is verbatim from BoardsColumn.
    if (!resolvedName) {
      return {
        boardId: String(item.board_id),
        boardName: "Unknown Board",
        infoContent: BOARD_ACCESS_INFO,
      };
    }

    return {
      boardId: String(item.board_id),
      boardName: resolvedName,
    };
  });
};

interface ToDiscrepancyRowsInput {
  syncId: string;
  dbItems: any[];
  discrepancyItems: any[];
  sharedColumns: any[];
  onSyncLatest: (columnType: string, columnTitle: string) => void;
  onViewHistory: (columnType: string, columnTitle: string) => void;
}

// Display reads the RAW column value, deliberately not the update's — buildColumnUpdates
// substitutes a dropdown's `values` array there because that is the resync payload shape, and
// DisplayValueComponent only resolves a cell's text when it is handed a truthy `columnValue`,
// so feeding it that array makes every dropdown read "Empty".
//
// Passed through untyped rather than narrowed to `typeof === "string"`. The pre-refactor path
// handed `update.value` to DisplayValueComponent with no narrowing at all, so any column whose
// value arrives as a truthy non-string (a pre-parsed object, a number) rendered its text before
// — a type guard here would turn those into "Empty" too, and no fixture would catch it because
// they all JSON.stringify.
const toDisplayValue = (update: columnValuesResync, matchingColumn: any): DisplayValue => ({
  value: matchingColumn?.value ?? null,
  text: update.text ?? null,
  exists: update.exists,
  // Monday's own snake_case, straight off the raw column. Carried because a board_relation
  // has NOTHING else to render: `value` above is null for that type on API 2025-10, so
  // without this the cell is blank no matter what the renderer does. Conditionally spread so
  // absent stays absent — an empty array would claim "this column holds no links", which is
  // not the same as "this column type has none".
  //
  // KNOWN GAP: simb-utils-ms's item-query-batch.ts does NOT yet request
  // `... on BoardRelationValue { linked_item_ids }`, so on this path the field never
  // arrives and board_relation cells stay blank. The subitem engine's queries do request it.
  // Deliberately left out of the status-colour hotfix — it changes a second column type's
  // behaviour and wants its own branch.
  ...(Array.isArray(matchingColumn?.linked_item_ids)
    ? { linkedItemIds: matchingColumn.linked_item_ids.map(String) }
    : {}),
  // Status label colour, from simb-utils-ms's item-query-batch.ts StatusValue fragment.
  // Conditionally spread so ABSENT stays absent: MondayValueCell distinguishes "this status
  // has no colour" (fall back to a theme token) from a colour that happens to be empty, and
  // normalising one into the other would paint an invented default.
  ...(matchingColumn?.label_style?.color
    ? { labelColor: matchingColumn.label_style.color as string }
    : {}),
});

/**
 * One grid row per shared column (ungrouped — Shared Items has no grouping concept, unlike the
 * Subitems tab). `id` is the row's stable identity; `meta` carries the same (columnType,
 * columnTitle) pair for callers that need it without parsing it back out of `id`.
 */
export const toDiscrepancyRows = ({
  syncId,
  dbItems,
  discrepancyItems,
  sharedColumns,
  onSyncLatest,
  onViewHistory,
}: ToDiscrepancyRowsInput): DiscrepancyGridRow[] =>
  (sharedColumns ?? []).map((sharedColumn: any) => {
    const columnType = sharedColumn.column_type;
    const columnTitle = sharedColumn.column_title;

    const valuesByBoard: Record<string, DisplayValue | null> = {};
    dbItems.forEach((item: any) => {
      const matchingColumn = findMatchingColumn(item, discrepancyItems, columnType, columnTitle);
      const update = toColumnUpdate(item, matchingColumn, columnType, columnTitle);
      valuesByBoard[String(update.board_id)] = toDisplayValue(update, matchingColumn);
    });

    const isNameRow = columnType === NAME_COLUMN_TYPE;

    return {
      id: `${syncId}-${columnType}-${columnTitle}`,
      label: columnTitle,
      valuesByBoard,
      // The grid already has a state for a name that differs across boards (the Subitems
      // tab's __name__ row); using it here keeps the two tabs' copy and colour the same.
      state: sharedColumn.discrepancy ? (isNameRow ? "name_drift" : "value_drift") : "ok",
      actions: [
        {
          key: "sync-latest",
          label: "Sync Latest",
          kind: "button",
          disabled: !sharedColumn.discrepancy,
          onClick: () => onSyncLatest(columnType, columnTitle),
        },
        // No history for a name: ViewHistoryModal reads column_values' activity, and a
        // rename is not a column change. Offering a button that opens an empty log would
        // be the same class of lie as a row claiming to be in sync.
        ...(isNameRow
          ? []
          : [
              {
                key: "view-history",
                label: "View History",
                kind: "icon" as const,
                icon: Time,
                onClick: () => onViewHistory(columnType, columnTitle),
              },
            ]),
      ],
      meta: { columnType, columnTitle },
    };
  });
